import { Layer, PickInfo } from 'deck.gl';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isEmpty } from 'underscore';
import moment from 'moment';
import Api from '../../../../api/Api';
import {
  IWmsProps,
} from '../../../../components/Map/Layer/DatasetMesh';
import Map from '../../../../components/Map/Map';
import guid from '../../../../generic/Guid';
import { toFixed } from '../../../../generic/toFixed';
import { StoreContext } from '../../../../store/store';
import { MarkerItem, MarkerItemToRestore, surfaceMaps } from '../../../../typings/home';
import IconTool from '../../../IconTool/IconTool';
import ResultPanel from '../../../ResultPanel/ResultPanel';
import styles from '../../Style/MainMap.module.scss';
import {
  MarkerLayer,
  LabelLayer,
  FWEPointLayer,
  InSituPointLayer,
  FWELAYER,
  INSITU_LAYER,
  INSITU_UPLOAD_LAYER,
  ExpandInSituLayer,
  INSITU_EXPANDED_LAYER,
} from '../../../../components/Map/Layer/MarkerLayer';
import LayerOverview from '../LayerOverview';
import PointLayerMenu from '../PointLayerMenu';
import DatasetZoneLayer, {
  FeatureCollectionLayer,
  GlobalDatasetZoneLayer,
  GlobaTextDatasetZoneLayer,
  GraticuleLayer,
  IPolygonLayer,
  IUploadedLayer,
  SelectedZoneLayer,
  SurfaceMapLayer
} from '../../../../components/Map/Layer/DatasetZoneLayer';
import {
  ConvertHexToRGBA,
  DownloadCheckboxKey,
  selectedColor,
} from '../../../../generic/common';
import { DownloadInfoItem } from '../../../../typings/analytics';
import { ShoppingItem } from '../../../../typings/shopping';
import { IDatasetBrief } from '../../../../typings/metadata';
import { css } from '@emotion/css';
import { ILabelLayer } from '../../../../typings/map';
import Footer from '../../../footer/Footer';
import LoadingContext from '../../../../context/loadingContext/LoadingContext';
import { withLoading } from '../../../../components/Loading/withLoading';
import { MARKERS_TO_RESTORE } from '../../../../generic/StaticVariable';
import { useNavigate, useLocation } from 'react-router-dom';
import { IFWEPoint, IInsituPoint } from '../../../../api/FileEndPoint';
import { useUserProvider } from '../../../../generic/hooks';
import InsituPointPopup from '../../../../components/Map/PointPopup/Insitu/InsituPointPopup';
import { createExpandedPoints, IMapCenter } from '../../../../components/Map/MapHelper';
import { MeshWMSLayer } from '../../../../components/Map/Layer/MeshWMSLayer';
import FwePointPopup from '../../../../components/Map/PointPopup/FWE/FwePointPopup';
import {Button, message, Modal, notification} from 'antd';
import {NOTIFICATION_TIME} from "../../../../generic/cookieUtils";

export const SEARCHPARAM = {
  COOR: 'coor',
  FWEPOINTNAME: 'fwePointName',
  SW: 'SW',
  HD: 'HD',
  WIND: 'Wind'
}

const acronymStyle = (sidePanelOpen: boolean) => {
  const right = sidePanelOpen ? '566px' : '16px';
  return css`
    padding: 8px 12px;
    text-align: center;
    position: absolute;
    bottom: 48px;
    color: #ffffff;
    background-color: #00A4EC;
    z-index: 5;
    display: block;
    right: ${right};
  `;
};

// We need to ensure that the new point name is not already in use
// e.g., as marker or as shopping item from previous visit
export const getNextFreeNumber = (
  markerCollections: MarkerItem[],
  shoppingItems: { [key: string]: { [key: string]: ShoppingItem } }
) => {
  let labels = markerCollections.map((x) => x.label);

  const shoppingKeys = Object.keys(shoppingItems);

  labels = labels.filter(label => !shoppingKeys.includes(label)).concat(shoppingKeys);

  let num = labels.length + 1;
  const name = 'P';
  while (labels.some((label) => label === name + num)) {
    num++;
  }
  return num.toString();
};

// When user add a point to the cart we need to ensure that point names are unique
// When logging in after point creation, point names of current session
// might conflict with point names in shopping cart of previous sessions
export const getDistinctName = (
  markerCollections: MarkerItem[],
  shoppingItems: { [key: string]: { [key: string]: ShoppingItem } },
  name: string
) => {
  let labels = markerCollections.map((x) => x.label);
  labels = labels.concat(Object.keys(shoppingItems));

  let num = 1;

  while (labels.some((label) => label === name + ' (' + num + ')')) {
    num++;
  }
  return name + ' (' + num + ')';
};

export default function MainMapContainer() {
  const {
    datasetBriefs,
    fweDatasetBriefs,
    setFWEModelDomains,
    fweModelDomains,
    markerCollections,
    setMarkerCollections,
    shoppingItems,
    datasetZones,
    setDatasetZones,
    showDatasetZones,
    legend,
    setLegend,
    addingPointToMap,
    setAddingPointToMap,
    showResultCollapse,
    setShowResultCollapse,
    uploadedShapefile,
    showUploadedShapefile,
    uploadedFeatures,
    setUploadingShapefile,
    visibleUploads,
    showLabelLayer,
    setOverridePointData,
    inSituPoints,
    setInSituPoints,
    showInSituPoints,
    fwePoints,
    setFWEPoints,
    showFWEPoints,
    activePopupPoint,
    showGlobalTextShapeFile,
    showGlobalShapeFile,
    setCloseValidation,
    showSurfaceMap,
    addPointName,
    user,
    setAddPointName,
    setShowSurfaceMap,
  } = useContext(StoreContext);
  const [isAuthorized] = useUserProvider()
  const loadingContext = useContext(LoadingContext);
  const { isLoading } = useContext(LoadingContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [layer, setLayer] = useState<Array<any>>(new Array<any>());
  const [meshUrl, setMeshUrl] = useState<IWmsProps>();
  const [updateMapBounds, setUpdateMapBounds] = useState<boolean>(false);
  const [dragPan, setDragPan] = useState(true);
  const [coordsMoved, setCoordsMoved] = useState(false);
  const [mouseCoords, setMouseCoords] = useState<Array<number>>(
    new Array<number>()
  );
  const [footerLabel, setFooterLabel] = useState('');
  const [meshResults, setMeshResults] = useState()
  const [legendStartEndValues, setLegendStartEndValues] = useState<[number | null, number | null]>([
    null, null
  ]);
  const [checkDataToRestore, setCheckDataToRestore] = useState<boolean>(false);
  const [globalShapeFile, setGlobalShapeFile] = useState<Array<any>>(new Array<any>());
  const [confirmationDeletePopup, setConfirmationDeletePopup] = useState(false);
  const [notAuthorizedUserPopup, setNotAuthorizedUserPopup] = useState(false);
  const [confirmationDeletePopupInsitu, setConfirmationDeletePopupInsitu] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [loadingSession, setLoadingSession] = useState(false)
  const [expandedPoints, setExpandedPoints] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const notificationTimerRef = useRef<any>(null);
  const expirationTimerRef = useRef<any>(null);
  const checkIntervalRef = useRef<any>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const activityLockRef = useRef(false);

  const didMountRef = useRef(false);

  const point = markerCollections.find((x) => x.active);

  function toggleResultCollapse(bool: boolean, _checked: boolean = true) {
      setShowResultCollapse(!bool);
  }

  let eventCleanup: (() => void) | null = null;

  const scheduleExpirationNotification = (expirationTime: number) => {
    clearTimeout(expirationTimerRef.current);

    const now = Date.now();
    const timeUntilExpiration = expirationTime - now;
    if (timeUntilExpiration > 0) {
      expirationTimerRef.current = setTimeout(() => {
        setIsModalOpen(true);
      }, timeUntilExpiration);
    } else {
      setIsModalOpen(true);
    }
  };

  const scheduleNotification = (expiration: number) => {
    clearTimeout(notificationTimerRef.current);

    const now = Date.now();
    const notifyTime = expiration - NOTIFICATION_TIME;

    if (notifyTime > now) {
      notificationTimerRef.current = setTimeout(() => {
        notification.warning({
          message: 'Your session is about to expire',
          description: <>It looks like you’ve been inactive for a while.<br/>
            For security reasons, your session will expire soon.
            If you&apos;re still here, simply take action to stay logged in.</>,
          duration: null,
        });
      }, notifyTime - now);
    }
  }

  const setupActivityMonitoring = (expiration: number) => {
    if (eventCleanup) {
      eventCleanup();
      eventCleanup = null;
    }

    const handleActivityAfterHalfway = async () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        if (activityLockRef.current) return;
        activityLockRef.current = true;

        const now = Date.now() + 60000;
        const timeUntilExpiration = expiration - now;
        const halfwayPoint = now + (timeUntilExpiration / 2);
        if (timeUntilExpiration <= halfwayPoint && timeUntilExpiration > 0 && !loadingSession) {
          try {
            const newExpiration = await fetchSession()
            if (newExpiration && newExpiration !== expiration) {
              clearTimeout(notificationTimerRef.current);
              clearTimeout(checkIntervalRef.current);
              clearTimeout(expirationTimerRef.current)

              scheduleNotification(newExpiration);
              scheduleExpirationNotification(newExpiration)

              if (eventCleanup) {
                eventCleanup();
                eventCleanup = null;
              }

              startMonitoring(newExpiration);
            }
          } finally {
            activityLockRef.current = false;
          }
        }
        console.groupEnd();
      }, 500);
    };
    const events = ['click', 'keypress', 'mousemove'];
    events.forEach(e => window.addEventListener(e, handleActivityAfterHalfway));

    eventCleanup = () => {
      events.forEach(e => window.removeEventListener(e, handleActivityAfterHalfway));
    };

    return eventCleanup;
  };

  const startMonitoring = (initialExpiration: number) => {
    const now = Date.now();
    const duration = initialExpiration - now;
    const halfwayPoint = now + (duration / 2);
    const initialDelay = Math.max(0, halfwayPoint - now);

    checkIntervalRef.current = setTimeout(() => {
      setupActivityMonitoring(initialExpiration);
    }, initialDelay);

    console.groupEnd();
  };

  const fetchSession = async () => {
    try {
      if(loadingSession) return
      setLoadingSession(true)
      const res = await Api.auth.getExpirationDate()
      const newExpiration = new Date(res as string).getTime()
      return newExpiration
    }catch (e) {
      console.log(e)
      return false
    }finally {
      setLoadingSession(false)
    }
  };

  useEffect(() => {
    if(!user || !user.authenticated) return
    fetchSession().then((initialExpiration) => {
      if (initialExpiration) {
        scheduleNotification(initialExpiration);
        scheduleExpirationNotification(initialExpiration)
        startMonitoring(initialExpiration);
      }
    });

    return () => {
      clearTimeout(notificationTimerRef.current);
      clearTimeout(expirationTimerRef.current);
      clearTimeout(checkIntervalRef.current);
    };
  }, [user]);

  useEffect(() => {
    if (checkDataToRestore && !isLoading) {
      const itemToRestore = localStorage.getItem(MARKERS_TO_RESTORE);
      if (itemToRestore) {
        localStorage.removeItem(MARKERS_TO_RESTORE);
        setOverridePointData(false);
        const markers: MarkerItemToRestore[] = JSON.parse(itemToRestore);
        const markersToSave = markers.map(x => {
          const temp: MarkerItem = {
            id: x.id,
            label: x.label,
            coordinates: x.coordinates,
            active: x.active,
            depth: 0,
            dataset: '',
            allDataset: {} as any,
            downloadPage: x.downloadPage.map(d => {
              return {
                datasetId: d.datasetId,
                radioKey: d.radioKey,
                dateRange: [moment(d.dateRange[0]), moment(d.dateRange[1])],
                options: []
              };
            }),
            shapeFiles: [],
            surfaceMaps: {
              enable: false,
              overlays: [],
              selectedLevel1: '',
              selectedLevel2: '',
              categories: [],
            } as surfaceMaps,
            fwePointName: x.fwePointName
          };
          return temp;
        });
        setMarkerCollections(markerCollections.concat(markersToSave));
      }

      setCheckDataToRestore(false);
    }
  }, [checkDataToRestore, toggleResultCollapse]);

  useEffect(() => {
    markerCollections &&
      markerCollections.length === 0 &&
      setMeshUrl(undefined);
  }, [markerCollections]);

  useEffect(() => {
    setShowResultCollapse(
      addingPointToMap
        ? addingPointToMap
        : markerCollections && markerCollections.length > 0
    );
  }, [markerCollections, addingPointToMap]);

  const [mapCenter, setMapCenter] = useState<IMapCenter | null>(null)
  useEffect(() => {
    if (didMountRef.current) return;

    if (datasetBriefs.length > 0) {
      const searchParams = new URLSearchParams(location.search);
      const coordinate = searchParams.get(SEARCHPARAM.COOR);

      if (coordinate) {
        const fwePointName = searchParams.get(SEARCHPARAM.FWEPOINTNAME);

        let decodedCoor = decodeURIComponent(coordinate);
        if (decodeURIComponent(decodedCoor) === decodedCoor) {
          decodedCoor = decodeURIComponent(decodedCoor);
        }

        try {
          const [longitude, latitude] = JSON.parse(decodedCoor);
          if (!isNaN(longitude) && !isNaN(latitude)) {
            setMapCenter({ longitude, latitude, zoom: 5 });
            onAddCoords(JSON.parse(decodedCoor), fwePointName || undefined);
          }
        } catch (error) {
          console.error('Error parsing coordinates:', error);
        }
      }

      didMountRef.current = true;
    }

  }, [datasetBriefs]);

  const onDrag = (info: PickInfo<MarkerItem>, e: any) => {
    //setDragPan(true)
  };

  const showCoordinates = (event: any) => {
    if (event.lngLat) {
      const lng: number = Math.round(event.lngLat.lng * 10000) / 10000;
      const lat: number = Math.round(event.lngLat.lat * 10000) / 10000;
      setMouseCoords([lng, lat]);
    }
  };

  const onResetCoordsMoved = () => {
    setCoordsMoved(false);
    if (point) {
      point.coordinatesToMove = undefined;
      setMarkerCollections([...markerCollections]);
    }
  };

  const onDragStart = (info: PickInfo<MarkerItem>, e: any) => {
    setDragPan(false);
  };

  const onDragEnd = (info: PickInfo<MarkerItem>, e: any) => {
    if (
      point &&
      info.coordinate &&
      info.coordinate.length === 2 &&
      info.object &&
      info.object.active === true
    ) {
      point.coordinatesToMove = [
        toFixed(info.coordinate[0], 6),
        toFixed(info.coordinate[1], 6),
      ];
      setMarkerCollections([...markerCollections]);
      setCoordsMoved(true);
    }
    setDragPan(true);
  };

  /**
   * Updates the map layers based on various conditions and inputs.
   *
   * This function performs the following actions:
   * 1. Processes uploaded shapefiles, if any.
   * 2. Handles dataset zone layers and selected zone layers.
   * 3. Updates the map bounds based on the surface map data, if available.
   * 4. Processes shapefiles that are set to display.
   * 5. Combines all the layers into a single array and sets the state.
   */
  const updateMapLayers = () => {
    // Process uploaded shapefiles
    let features = new Array<IUploadedLayer>();
    if (showUploadedShapefile && uploadedFeatures) {
      features = uploadedFeatures;
      setUploadingShapefile(false);
    }

    // Initialize polygon layers and selected polygon layers
    let polygonLayers = new Array<IPolygonLayer>();
    let selectedPolygonLayer = new Array<IPolygonLayer>();
    let surfaceMapLayerData = new Array<IPolygonLayer>();
    if (showDatasetZones) {
      polygonLayers = polygonLayers.concat(datasetZones);
    }

    if (showFWEPoints){
      polygonLayers = polygonLayers.concat(fweModelDomains);
    }

    // Set colors for selected zones
    const lineColor = ConvertHexToRGBA(selectedColor);
    const filledColor = ConvertHexToRGBA(selectedColor, 0.2);
    datasetZones.forEach((zone) => {
      if (zone.id === point?.dataset) {
        const selectedZone: IPolygonLayer = {
          id: zone.id,
          coordinates: zone.coordinates,
          lineColor: lineColor,
          color: filledColor,
        };
        selectedPolygonLayer = selectedPolygonLayer.concat(selectedZone);
      }
    });

    // Update map bounds based on surface map data
    if (
      point &&
      showSurfaceMap &&
      point.surfaceMaps.selectedLevel1 !== '' &&
      point.surfaceMaps.selectedLevel2 !== ''
    ) {
      const surfaceMap = point.surfaceMaps.overlays.find(
        (o) =>
          o.Level1 === point?.surfaceMaps.selectedLevel1 &&
          o.Level2 === point?.surfaceMaps.selectedLevel2
      );

      if (surfaceMap?.data !== undefined) {
        surfaceMapLayerData = surfaceMap?.data;
        setUpdateMapBounds(true);
      } else {
        setUpdateMapBounds(false);
      }
    } else {
      setUpdateMapBounds(false);
    }

    // Process shapefiles set to display
    point?.shapeFiles.forEach((shape) => {
      if (shape.IsDisplay && shape.data !== undefined)
        polygonLayers = polygonLayers.concat(shape.data);
    });

    // Combine all layers
    const layers: Array<Layer<any, any>> = [
      SurfaceMapLayer(surfaceMapLayerData) as Layer<any, any>,
      MeshWMSLayer({...meshUrl} as IWmsProps) as any,
      DatasetZoneLayer(polygonLayers) as Layer<any, any>,
      GlobalDatasetZoneLayer(showGlobalShapeFile ? globalShapeFile : []) as Layer<any, any>,
      GlobaTextDatasetZoneLayer(showGlobalTextShapeFile ? globalShapeFile : []) as Layer<any, any>,
      SelectedZoneLayer(selectedPolygonLayer) as Layer<any, any>,
      FWEPointLayer(showFWEPoints ? fwePoints : []) as Layer<any, any>,
      ExpandInSituLayer(showInSituPoints && expandedPoints ? expandedPoints : []),
      InSituPointLayer(showInSituPoints ? inSituPoints: []) as Layer<any, any>,
      ...FeatureCollectionLayer(features, visibleUploads) as Array<Layer<any, any>>,
    ];

    const layersAndGraticule: Array<Layer<any, any>> = layers;
    const graticuleLabels: ILabelLayer[] = [];
    const pointLabel: ILabelLayer[] = showLabelLayer
      ? markerCollections.map((point: MarkerItem) => {
        return {
          color: [142, 7, 77],
          name: point.label,
          coordinates: point.coordinates,
          active: point.active,
          textSize: 24,
          textAnchor: 'middle',
        };
      })
      : [];
    const layersAndLabels = layersAndGraticule.concat([
      LabelLayer(graticuleLabels.concat(pointLabel)) as Layer<any, any>,
      ...MarkerLayer(showLabelLayer ? markerCollections : [], onDrag, onDragStart, onDragEnd) as Array<Layer<any, any>>
    ]);

    setLayer(layersAndLabels);
  };

  // Invoke the updateMapLayers function with the withLoading helper whenever any dependency changes
  useEffect(() => {
    updateMapLayers()
  }, [
    markerCollections,
    datasetZones,
    showDatasetZones,
    point,
    uploadedShapefile,
    showUploadedShapefile,
    uploadedFeatures,
    visibleUploads,
    showLabelLayer,
    fwePoints,
    showFWEPoints,
    inSituPoints,
    showInSituPoints,
    fweModelDomains,
    meshUrl,
    showGlobalTextShapeFile,
    globalShapeFile,
    expandedPoints,
    showGlobalShapeFile,
  ]);


  useEffect(() => {
    const getFWEPoints = async () => {
      const fwe = await Api.FWEPoint.getFWEPoints();
      setFWEPoints(fwe)
    }
    const getInSitu = async () => {
      const insitu = await Api.insituPoint.getInsituPoints()
      setInSituPoints(insitu)
    }
    if (isAuthorized) {
      getFWEPoints();
      getInSitu();
    }
    else {
      setInSituPoints([]);
      setFWEPoints([]);
    }
  }, [isAuthorized])

  /**
   * Fetches the dataset extent polygons for visible datasets and updates the map.
   *
   * This function performs the following actions:
   * 1. Checks if there are any datasetBriefs to process.
   * 2. Filters out confidential datasets that should not be shown.
   * 3. Calls the API to fetch the dataset extent polygons.
   * 4. Updates the state with the fetched polygons and sets the map view.
   */
  async function fetchVisibleDatasetPolygons(
    options: { isMounted?: { current: boolean } } = {}
  ) {
    const { isMounted } = options;

    if (datasetBriefs?.length === 0) return;

    if (isMounted?.current) {
      // ids are input for GetAllMoodSpacedefinition call that returns the dataset extent polygons shown on the map
      // Some datasets are confidential and need to be hidden for users without view rights

      // Fetch dataset extent polygons for non-confidential datasets
      const moodBriefs =  datasetBriefs.filter((d) => d.IsFWE === false)
      const moodIds =  moodBriefs.map((d) => {
        return d.Id;
      })
      setDatasetZones(
        await Api.downloadRequest.getAllMoodSpaceDefinition(moodIds)
      );

      // Restore Data
      setCheckDataToRestore(true);
    }
  }

  async function fetchVisibleFWEDatasetPolygons(options: { isMounted?: { current: boolean } } = {}
    ) {
      const { isMounted } = options;

      if (fweDatasetBriefs?.length === 0) return;

      if (isMounted?.current) {
        // Fetch dataset extent polygons for FWE datasets
        setFWEModelDomains(
          await Api.downloadRequest.getAllFWESpaceDefinition(
            fweDatasetBriefs.map((d) => {
              return d.Id;
            })
          )
        );

      }
    }

  // Invoke the fetchVisibleDatasetPolygons with the withLoading helper whenever the datasetBriefs dependency changes
  useEffect(() => {
    const isMounted = { current: true };
    withLoading(fetchVisibleDatasetPolygons, loadingContext, {
      isMounted,
      source: 'fetchVisibleDatasetPolygons',
    });

    // Cleanup function
    return () => {
      isMounted.current = false; // Set the flag to false when the component is unmounted
    };
  }, [datasetBriefs]);

    // Invoke the fetchVisibleFWEDatasetPolygons with the withLoading helper whenever the fweDatasetBriefs dependency changes
    useEffect(() => {
      const isMounted = { current: true };

      withLoading(fetchVisibleFWEDatasetPolygons, loadingContext, {
        isMounted,
        source: 'fetchVisibleFWEDatasetPolygons',
      });

      // Cleanup function
      return () => {
        isMounted.current = false; // Set the flag to false when the component is unmounted
      };
    }, [fweDatasetBriefs]);

  /**
   * Retrieves the acronym for the selected dataset from the datasetBriefs list.
   *
   * This function performs the following actions:
   * 1. Checks if the point, point.dataset, datasetBriefs, and datasetBriefs.length are defined and valid.
   * 2. Finds the datasetBrief that matches the point's dataset ID.
   * 3. Returns the acronym for the matching datasetBrief if found.
   *
   * @callback
   * @returns {string | undefined} The acronym for the selected dataset or undefined if not found.
   */
  const getAcronym = useCallback(() => {
    if (point && point.dataset && datasetBriefs && datasetBriefs.length > 0) {
      const brief = datasetBriefs.find(
        (datasetBrief: IDatasetBrief) => datasetBrief.Id === point.dataset
      );
      if (brief !== undefined) {
        return brief.Acronym;
      }
    }
  }, [point && point.dataset, datasetBriefs && datasetBriefs.length > 0]);

  useEffect(() => {
    let isMounted = true;

    const getGlobalOverlay = async () => {
      if (Api.fileEndPoint) {
        try {
          const layerData = await Api.fileEndPoint.getOverlayGeoJson(
            'Merged_OWF_Many',
            'Offshore Wind Farms',
            'Offshore Wind Farms',
            '#B5651D'
          );
          if (isMounted) setGlobalShapeFile(layerData);
        } catch (error) {
          console.error('Error fetching global overlay:', error);
        }
      }
    };

    const initializeOverlay = async () => {
      try {
        let retries = 0;
        while (!Api.SERVER_DOMAIN && retries < 100) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }

        if (!Api.SERVER_DOMAIN) {
          throw new Error('Server domain not available after maximum retries');
        }
        if (isMounted) {
          withLoading(getGlobalOverlay, loadingContext, {
            source: 'getGlobalLayer',
          });
        }
      } catch (error) {
        console.error('Error initializing overlay:', error);
        if (isMounted) console.error('Failed to initialize overlay: Server domain not available');
      }
    };
    initializeOverlay();

    return () => {
      isMounted = false;
    };
  }, []);


  /**
   * Fetches overlay data for the selected dataset and updates the point's shape files and surface maps.
   *
   * This function performs the following actions:
   * 1. Checks if the point and point.dataset are defined and valid.
   * 2. Resets the map's legend, shape files, surface maps, and marker collections to their default values.
   * 3. Calls the API to fetch the overlay data for the selected dataset.
   * 4. Updates the point's shape files and surface maps with the fetched data.
   * 5. Enables surface maps if they exist for the dataset.
   */
  async function fetchOverlayData(
    options: { isMounted?: { current: boolean } } = {}
  ) {
    const { isMounted } = options;

    if (point === undefined || point.dataset === '') return;

    if (isMounted?.current) {
      // Reset map state to defaults
      setLegend({ ...legend, display: false });
      point.shapeFiles = [];
      setMarkerCollections([...markerCollections]);

      // Fetch overlay data and update map statem
      point.shapeFiles = await Api.fileEndPoint.getOverlayTree2(point.dataset);
      point.surfaceMaps = await Api.fileEndPoint.getOverlayTree(point.dataset);
      if(showSurfaceMap){
        setShowSurfaceMap(point.surfaceMaps.categories.length > 0)
      }
      setMarkerCollections([...markerCollections]);
    }
  }

  // Invoke the fetchOverlayData function with the withLoading helper whenever the point's dataset changes
  useEffect(() => {
    const isMounted = { current: true };

    withLoading(fetchOverlayData, loadingContext, {
      isMounted,
      source: 'fetchOverlayData',
    });

    // Cleanup function
    return () => {
      isMounted.current = false; // Set the flag to false when the component is unmounted
    };
  }, [point?.dataset]);

  useEffect(() => {
    if (mouseCoords && mouseCoords.length > 1) {
      setFooterLabel(
        'Longitude [°E], Latitude [°N]: ' +
        mouseCoords[0] +
        ', ' +
        mouseCoords[1]
      );
    }
  }, [mouseCoords]);

  //  do map click or not
  function triggerMark(bool: boolean) {
    setAddingPointToMap(bool);
  }

  function getLabelName(): string {
    return 'P' + getNextFreeNumber(markerCollections, shoppingItems);
  }

  function downloadCheckboxList() {
    const rangeDate: [moment.Moment, moment.Moment] = [
      moment().subtract(1, 'year'),
      moment(),
    ];
    return [
      {
        datasetId: '',
        dateRange: rangeDate,
        radioKey: DownloadCheckboxKey.SW,
        options: [],
      },
      {
        datasetId: '',
        dateRange: rangeDate,
        radioKey: DownloadCheckboxKey.HD,
        options: [],
      },
      {
        datasetId: '',
        dateRange: rangeDate,
        radioKey: DownloadCheckboxKey.Wind,
        options: [],
      },
    ] as DownloadInfoItem[];
  }

  /**
   * Adds a new active point to the marker collection at the specified coordinates.
   *
   * @async
   * @param coordinates The coordinates of the new point to add.
   * @returns A promise that resolves to void when the function completes.
   */
  async function triggerMapClick(coordinates: number[], fwePointName?: string, insituPointName?: string) {
    setAddingPointToMap(false);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set(SEARCHPARAM.COOR, JSON.stringify(coordinates));
    if (fwePointName){
      const fwePointNameEncoded = encodeURIComponent(fwePointName).trim()
      searchParams.set(SEARCHPARAM.FWEPOINTNAME, fwePointNameEncoded)
    }
    navigate(`/?${searchParams.toString()}`);
    markerCollections.map((point) => (point.active = false));
    setCloseValidation(true)
    const temp: MarkerItem = {
      id: guid(),
      label: addPointName && !isEmpty(addPointName) ? addPointName : getLabelName(),
      coordinates: [toFixed(coordinates[0], 6), toFixed(coordinates[1], 6)],
      active: true,
      depth: 0,
      dataset: '',
      allDataset: {} as any,
      downloadPage: downloadCheckboxList(),
      shapeFiles: [],
      surfaceMaps: {
        overlays: [],
        selectedLevel1: '',
        selectedLevel2: '',
        categories: [],
      } as surfaceMaps,
      fwePointName: fwePointName,
      insituPointName: insituPointName,
    };
    setAddPointName('')
    setMarkerCollections(markerCollections.concat(temp));
  }

  async function changeMeshLayerProp(id: string) {
    setMeshUrl(undefined);
    if (isEmpty(id)) return;
    const realPreview = await Api.metadata.getMetadataPreviewText(id);
    if (!realPreview) {
      return;
    }
    const data = await Api.dataPreview.getDataPreview(realPreview);
    if (data && data.obj) {
      const parsedObject = JSON.parse(data.obj);
      if (parsedObject && parsedObject.p_wms.p_wmsGeoOption) {
        const options = parsedObject.p_wms.p_wmsGeoOption;
        const layer = options.layers;
        const style = options.styles;
        const date = options.sourceDateTime;

        if (layer && style && date) {
          setMeshUrl({
            layers: layer,
            styles: style,
            sourceItem: '1',
            sourceDateTime: date,
            width: 256,
            height: 256,
          } as IWmsProps);
        }
      }
    }
  }

  /**
   * Calls the `triggerMapClick` function with the specified coordinates and wrapped in a loading context.
   *
   * @param coords The coordinates of the new point to add.
   * @returns void.
   */
  const onAddCoords = (coords: number[], fwePointName?: string) => {
    withLoading(() => triggerMapClick(coords, fwePointName), loadingContext, {
      source: 'onAddCoords',
    });
  };

  const [fwePoint, setFwePoint] = useState<IFWEPoint | null>(null);
  const [insituPoint, setInsituPoint] = useState<IInsituPoint | null>(null)
  const [expandedPoint, setExpandedPoint] = useState<IInsituPoint | null>(null)
  const handlePopupCoordinatesChanged = (pointParam: IFWEPoint | IInsituPoint, layerId: string) => {
    setFwePoint(null);
    setInsituPoint(null)
    if(layerId === INSITU_EXPANDED_LAYER) {
      setExpandedPoint(pointParam as IInsituPoint)
    } else if (layerId === FWELAYER) {
      setFwePoint(pointParam as IFWEPoint);
    } else if (layerId === INSITU_LAYER || layerId === INSITU_UPLOAD_LAYER) {
      const samePositionPoints = inSituPoints.filter(p =>
        p.position[0] === pointParam.position[0] &&
        p.position[1] === pointParam.position[1]
      );

      if(samePositionPoints.length === 1){
        setInsituPoint(pointParam as IInsituPoint)
      }
      else {
        const expanded = createExpandedPoints(samePositionPoints);
        setExpandedPoints(expanded);
      }
    }
  };

  const handleClosePopup = () => {
    setFwePoint(null);
    setInsituPoint(null)
    setExpandedPoint(null)
  }

  const popupContent = useCallback(() => {
    if (activePopupPoint === FWELAYER && fwePoint) {
      return (
        <FwePointPopup
          setNotAuthorizedUserPopup={setNotAuthorizedUserPopup}
          setConfirmationDeletePopup={setConfirmationDeletePopup}
          loadingDelete={loadingDelete}
          setLoadingDelete={setLoadingDelete}
          coordinates={fwePoint.position}
          name={fwePoint.name}
          createNewPoint={triggerMapClick}
          fwePointOrigin={{
            tenant: fwePoint.tenant,
            folder: fwePoint.folder,
          }}
          datasetId={fwePoint.datasetId}
          fweUrl={Api.FWE_URL}
          handleClose={() => setFwePoint(null)}
        />
      );
    } else if ((activePopupPoint === INSITU_LAYER) && insituPoint) {
      return (
        <InsituPointPopup
          coordinates={insituPoint.position}
          createNewPoint={triggerMapClick}
          name={insituPoint.name}
          timePeriod={{
            startDate: insituPoint.startDate,
            endDate: insituPoint.endDate
          }}
          source={insituPoint.source}
          handleClose={() => setInsituPoint(null)}
          setConfirmationDeletePopup={setConfirmationDeletePopupInsitu}
          loadingDelete={loadingDelete}
          setLoadingDelete={setLoadingDelete}
          type={insituPoint.type}
        />
      )
    } else if ((activePopupPoint === INSITU_EXPANDED_LAYER) && expandedPoint) {
      return (
        <InsituPointPopup
          coordinates={expandedPoint.originalPosition ?? expandedPoint.position}
          createNewPoint={triggerMapClick}
          name={expandedPoint.name}
          timePeriod={{
            startDate: expandedPoint.startDate,
            endDate: expandedPoint.endDate
          }}
          source={expandedPoint.source}
          handleClose={() => setExpandedPoint(null)}
          setConfirmationDeletePopup={setConfirmationDeletePopupInsitu}
          loadingDelete={loadingDelete}
          setLoadingDelete={setLoadingDelete}
          type={expandedPoint.type}
        />
      )
    }
    return null
  }, [activePopupPoint, fwePoint, insituPoint, expandedPoint, loadingDelete]);

  const activePopupCoordinates = useCallback(() => {
    if (activePopupPoint === FWELAYER && fwePoint) {
      return fwePoint.position;
    } else if ((activePopupPoint === INSITU_LAYER || activePopupPoint === INSITU_UPLOAD_LAYER) && insituPoint) {
      return insituPoint.position;
    } else if ((activePopupPoint === INSITU_EXPANDED_LAYER) && expandedPoint){
      return expandedPoint.position
    }
    return null
  }, [fwePoint, insituPoint, expandedPoint, activePopupPoint])


  const handleDeleteDatasetInsitu = useCallback(async () => {
    try {
      setLoadingDelete(true)
      const point = insituPoint ?? expandedPoint

      if (!point) {
        throw new Error('InSitu point does not exist')
      }

      const res = await Api.insituPoint.deleteInsituPoint(point.name || '');

      if (res?.Error) {
        const errMessage = res.Error?.ErrorMessage ?? `Failed to delete ${point.name}`
        throw new Error(errMessage)
      }

      const insitus = await Api.insituPoint.getInsituPoints();

      message.success('Delete completed');
      setConfirmationDeletePopupInsitu(false)
      handleClosePopup();
      setInSituPoints(insitus)
      setExpandedPoints([])

    } catch (error: any) {
      message.error(error.message)

    } finally {
      setLoadingDelete(false)
    }
  }, [handleClosePopup]);

  const handleDeleteDataset = useCallback(async () => {
    try {
      setLoadingDelete(true)
      await Api.service.deleteFWEdataset(fwePoint?.datasetId ?? '');
      const fwe = await Api.FWEPoint.getFWEPoints();
      message.success('Dataset removed from MOOD');
      setConfirmationDeletePopup(false)
      handleClosePopup();
      setFWEPoints(fwe)
    } catch (error) {
      console.error('Error deleting dataset:', error);
    } finally {
      setLoadingDelete(false)
    }
  }, [handleClosePopup]);

  const handleRequestDeleteDataset = useCallback(async () => {
    try {
      setLoadingDelete(true)
      await Api.service.requestDeleteFWEdataset(fwePoint?.datasetId ?? '');
      message.success('Request Sent');
      setNotAuthorizedUserPopup(false)
      handleClosePopup();
    } catch (error) {
      console.error('Error sending request deleting dataset:', error);
    } finally {
      setLoadingDelete(false)
    }
  }, [handleClosePopup]);

  const handleLogin = React.useCallback(() => {
    (window.top || window).location.assign(
      `${Api.SERVER_DOMAIN}${Api.IISSite}/auth/signin`
    );
  }, []);

  const resolvedPopupContent = popupContent();
  const activeCoordinates = activePopupCoordinates()

  return (
    <div className={styles.map}>
      {datasetZones?.length > 0 && (
        <PointLayerMenu>
          <LayerOverview
            legendStartEndValues={legendStartEndValues}
            setLegendStartEndValues={setLegendStartEndValues}
            onMeshControl={changeMeshLayerProp}
            result={meshResults}
            setResult={setMeshResults} />
        </PointLayerMenu>
      )}
      <IconTool
        legendStartEndValues={legendStartEndValues}
        setLegendStartEndValues={setLegendStartEndValues}
        collapseOpen={!showResultCollapse}
        onCollapse={toggleResultCollapse}
        result={meshResults}
        setResult={setMeshResults}
      />
      {point?.dataset && (
        <div className={acronymStyle(showResultCollapse)}>{getAcronym()}</div>
      )}
      <Footer mouseCoords={footerLabel}></Footer>
      <ResultPanel
        coordsMoved={coordsMoved}
        onResetCoordsMove={onResetCoordsMoved}
        isMark={addingPointToMap}
        open={showResultCollapse}
        onMeshControl={changeMeshLayerProp}
        addCoords={onAddCoords}
        onMark={triggerMark}
      />
      <Map
        center={mapCenter}
        dragPan={dragPan}
        isMark={addingPointToMap}
        onClick={triggerMapClick}
        layers={layer}
        popupContent={resolvedPopupContent}
        popupCoordinates={activeCoordinates}
        onPopupCoordinatesChanged={handlePopupCoordinatesChanged}
        onClosePopup={handleClosePopup}
        updateBounds={updateMapBounds}
        onMouseMove={showCoordinates}
      >
        {/* <DatasetMesh {...meshUrl!} /> */}
      </Map>
      <Modal
        visible={confirmationDeletePopup}
        onOk={handleDeleteDataset}
        onCancel={() => setConfirmationDeletePopup(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmationDeletePopup(false)} disabled={loadingDelete}>
            Cancel
          </Button>,
          <Button key="ok" type="primary" onClick={handleDeleteDataset} loading={loadingDelete}>
            Confirm
          </Button>
        ]}
      >
        <p>On confirmation the dataset will be removed from MOOD</p>
      </Modal>
      <Modal
        visible={notAuthorizedUserPopup}
        onOk={handleRequestDeleteDataset}
        onCancel={() => setNotAuthorizedUserPopup(false)}
        closable={false}
        footer={[
          <Button key="cancel" onClick={() => setNotAuthorizedUserPopup(false)} disabled={loadingDelete}>
            Cancel
          </Button>,
          <Button key="ok" type="primary" onClick={handleRequestDeleteDataset} loading={loadingDelete}>
            Confirm
          </Button>
        ]}
      >
        <p>MIKE MS datasets can only be removed by the project owners, request sent to owners</p>
      </Modal>
      <Modal
        visible={confirmationDeletePopupInsitu}
        onOk={handleDeleteDataset}
        onCancel={() => setConfirmationDeletePopupInsitu(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmationDeletePopupInsitu(false)} disabled={loadingDelete}>
            Cancel
          </Button>,
          <Button key="ok" type="primary" onClick={handleDeleteDatasetInsitu} loading={loadingDelete}>
            Confirm
          </Button>
        ]}
      >
        <p>On confirmation the dataset will be removed from MOOD</p>
      </Modal>
      <Modal
        title="Session Expired"
        visible={isModalOpen}
        closable={false}
        footer={[
          <Button
            key="reload"
            type="primary"
            onClick={handleLogin}
          >
            Sign In
          </Button>
        ]}
      >
        <p>Your session has expired. Please sign in again.</p>
      </Modal>
    </div>
  );
}
